package fr.arcplex.network.reporting

import fr.arcplex.network.http.Serializer
import fr.arcplex.network.http.createNetworkHttpClient
import fr.arcplex.network.logging.Logger
import io.ktor.client.HttpClient
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.get
import io.ktor.client.request.headers
import io.ktor.serialization.kotlinx.json.json
import kotlin.concurrent.Volatile
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

sealed interface SendOutcome {
    /** The backend accepted the event; it must never be sent again. */
    data object Delivered : SendOutcome

    /** Transient failure (offline, timeout, 5xx). Keep the event and try again later. */
    data object RetryLater : SendOutcome

    /** The backend will never accept the event (4xx, or no endpoint). Drop it. */
    data object Drop : SendOutcome
}

interface EventTransport {
    suspend fun send(event: ReportEvent): SendOutcome
}

/**
 * Drains the [EventQueue] towards an [EventTransport], oldest event first.
 *
 * Delivery is at-least-once and strictly ordered: a failed event blocks the ones behind it
 * (a `play` must reach the backend before its `stop`), and is only dropped when the backend
 * permanently rejects it or after [maxAttempts]. A transient failure backs off exponentially
 * so an offline device retries without hammering the network or the battery.
 *
 * The reporter owns [scope] and is never stopped by [PageManager.stop]: that cancels the page
 * manager's scope on Android's `onUserLeaveHint`, and sharing it would kill in-flight sends.
 */
class Reporter(
    private val queue: EventQueue,
    private val transport: EventTransport,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Default),
    private val backoffMillis: List<Long> = DEFAULT_BACKOFF_MILLIS,
    private val maxAttempts: Int = DEFAULT_MAX_ATTEMPTS
) {
    companion object {
        val DEFAULT_BACKOFF_MILLIS = listOf(5_000L, 15_000L, 60_000L, 300_000L, 900_000L)
        const val DEFAULT_MAX_ATTEMPTS = 10
    }

    private val flushMutex = Mutex()
    private var retryJob: Job? = null

    /**
     * True while a backoff timer is running. New events are queued but do not trigger a drain,
     * otherwise a burst of events produced while offline would retry once per event and defeat
     * the backoff entirely.
     */
    @Volatile
    private var retryPending = false
    private var backoffIndex = 0

    /** Replays whatever survived a previous run. Call once, at construction. */
    fun start() {
        scope.launch { flush() }
    }

    fun stop() {
        retryJob?.cancel()
        retryJob = null
        retryPending = false
        scope.cancel()
    }

    /** Never blocks the caller: the player callbacks call this from the UI thread. */
    fun report(type: ReportEventType, payload: Map<String, String> = emptyMap()) {
        scope.launch {
            queue.enqueue(type, payload)
            flush()
        }
    }

    suspend fun flush() {
        if (retryPending) return
        // A drain may already be running; the events it has not seen yet will be picked up
        // by the loop below, so there is nothing to queue up behind it.
        if (!flushMutex.tryLock()) return
        try {
            while (true) {
                val event = queue.peekBatch(1).firstOrNull()
                if (event == null) {
                    backoffIndex = 0
                    return
                }
                when (transport.send(event)) {
                    SendOutcome.Delivered -> queue.ack(event.id)

                    SendOutcome.Drop -> queue.fail(event.id, permanent = true)

                    SendOutcome.RetryLater -> {
                        val attempts = event.attempts + 1
                        val giveUp = attempts >= maxAttempts
                        queue.fail(event.id, permanent = giveUp)
                        if (!giveUp) {
                            scheduleRetry()
                            return
                        }
                        Logger.e("Reporting", "giving up on ${event.type} ${event.id} after $attempts attempts")
                    }
                }
            }
        } finally {
            flushMutex.unlock()
        }
    }

    private fun scheduleRetry() {
        retryPending = true
        val delayMs = backoffMillis.getOrElse(backoffIndex) { backoffMillis.last() }
        backoffIndex = (backoffIndex + 1).coerceAtMost(backoffMillis.lastIndex)
        retryJob?.cancel()
        retryJob = scope.launch {
            delay(delayMs)
            retryPending = false
            flush()
        }
    }
}

/**
 * Sends player events to the legacy generique API.
 *
 * This deliberately does NOT go through [ApiClient.requestJson]: that path caches every GET
 * 2xx response, so a second `video_started` within the 10 minute TTL would be answered from
 * the cache and never reach the server.
 */
class LegacyEventTransport(
    private val baseUrl: String,
    private val userToken: () -> String?,
    private val prefix: String = "",
    private val version: String = "5",
    allowInvalidCertificates: Boolean = false,
    private val client: HttpClient = createNetworkHttpClient(allowInvalidCertificates) {
        install(ContentNegotiation) { json(Serializer.json) }
    }
) : EventTransport {

    override suspend fun send(event: ReportEvent): SendOutcome {
        val endpoint = when (event.type) {
            ReportEventType.VIDEO_STARTED -> "video_started"
            ReportEventType.VIDEO_STOPPED -> "video_stopped"
            else -> {
                // Pause, seek and telemetry have no endpoint yet; dropping them keeps the
                // queue moving instead of blocking every later event behind them.
                Logger.e("Reporting", "no endpoint for ${event.type} yet, dropping ${event.id}")
                return SendOutcome.Drop
            }
        }

        return try {
            val response = client.get("${baseUrl.removeSuffix("/")}/api/generique$prefix/V$version/$endpoint") {
                headers {
                    append("Accept", "application/json")
                    // Mirrors what DefaultApi sends for these endpoints.
                    append("pretty-url", "")
                    append("The-Origin", "")
                    userToken()?.let { append("user-token", it) }
                }
            }
            when (response.status.value) {
                in 200..299 -> SendOutcome.Delivered
                in 400..499 -> SendOutcome.Drop
                else -> SendOutcome.RetryLater
            }
        } catch (e: Exception) {
            Logger.e("Reporting", "sending ${event.type} ${event.id} failed: ${e.message}")
            SendOutcome.RetryLater
        }
    }
}
